// src/pages/EditProfile.jsx
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase/firebaseConfig";
import { UserContext } from "./context/UserContext";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { toast } from "react-toastify";

// 🎨 Styled Components
const Container = styled.div`
  max-width: 700px;
  margin: 3rem auto;
  padding: 2.5rem;
  background: #1e1e2f;
  border-radius: 16px;
  color: white;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 2rem;
  font-weight: bold;
  background: linear-gradient(90deg, #00d4ff, #ff7a18);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.95rem;
  color: #ddd;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid #333;
  border-radius: 10px;
  background: #2a2a3d;
  color: white;
  outline: none;
  transition: border 0.2s;

  &:focus {
    border-color: #00d4ff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid #333;
  background: #2a2a3d;
  color: white;
  outline: none;

  &:focus {
    border-color: #ff7a18;
  }
`;

const Button = styled.button`
  padding: 1rem;
  width: 100%;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: 0.25s;
  margin-top: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 5px 15px rgba(255, 0, 102, 0.5);
  }
`;

const AvatarWrapper = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid #00d4ff;
  background: #2a2a3d;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.4);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    color: #888;
    width: 60%;
    height: 60%;
  }
`;

const UploadLabel = styled.label`
  margin-top: 1rem;
  display: inline-block;
  background: #00d4ff;
  color: #111;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #00a3cc;
  }

  input {
    display: none;
  }
`;

// ================== Professions List ==================
const professions = [
  "Software Engineer",
  "Frontend Developer",
  "Backend Developer",
  "Fullstack Developer",
  "React Developer",
  "React Native Developer",
  "Android Developer",
  "iOS Developer",
  "Mobile App Developer",
  "DevOps Engineer",
  "Cloud Engineer",
  "Data Scientist",
  "Machine Learning Engineer",
  "AI Engineer",
  "Deep Learning Engineer",
  "UI/UX Designer",
  "Product Designer",
  "Game Developer",
  "Security Engineer",
  "Blockchain Developer",
  "IoT Developer",
  "Kotlin Developer",
  "Python Developer",
  "Java Developer",
  "Go Developer",
  "Rust Developer",
  "PHP Developer",
  "WordPress Developer",
  "Shopify Developer",
  "Salesforce Developer",
  "QA Engineer",
  "Automation Tester",
  "Solutions Architect",
  "System Administrator",
  "Network Engineer",
  "Linux Administrator",
  "Site Reliability Engineer",
  "Data Analyst",
  "Cybersecurity Analyst",
  "Ethical Hacker",
  "Penetration Tester",
  "Cloud Security Engineer",
  "Web3 Developer",
  "Solidity Developer",
  "Software Architect",
  "Tech Lead",
  "Engineering Manager",
  "CTO",
  "Other (Custom Input)",
];

// ================== Component ==================
export default function EditProfile() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    name: "",
    phone: "",
    profession: "",
    customProfession: "",
    profilePic: "",
  });

  useEffect(() => {
    if (!user?.id) return;
    const fetchProfile = async () => {
      const refDoc = doc(db, "users", user.id);
      const snap = await getDoc(refDoc);
      if (snap.exists()) {
        setProfileData(snap.data());
      }
    };
    fetchProfile();
  }, [user]);

  const handleChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `users/${user.id}/profile.jpg`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    setProfileData({ ...profileData, profilePic: url });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const refDoc = doc(db, "users", user.id);
      await updateDoc(refDoc, {
        name: profileData.name,
        phone: profileData.phone,
        profession:
          profileData.profession === "Other (Custom Input)"
            ? profileData.customProfession
            : profileData.profession,
        profilePic: profileData.profilePic,
      });
      toast.success("Profile Updated Successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Profile Updated Failed");
    }
  };

  return (
    <Container>
      <Title>Edit Your Profile</Title>

      {/* Profile Pic */}
      <AvatarWrapper>
        <Avatar>
          {profileData.profilePic ? (
            <img src={profileData.profilePic} alt="Profile" />
          ) : (
            <User />
          )}
        </Avatar>

        <UploadLabel>
          Change Profile Image
          <input type="file" accept="image/*" onChange={handleImageUpload} />
        </UploadLabel>
      </AvatarWrapper>
      {/* Form */}
      <Form onSubmit={handleSave}>
        <Field>
          <Label>Name</Label>
          <Input name="name" value={profileData.name} onChange={handleChange} />
        </Field>

        <Field>
          <Label>Phone (with country code)</Label>
          <Input
            name="phone"
            placeholder="+91 9876543210"
            value={profileData.phone}
            onChange={handleChange}
          />
        </Field>

        <Field>
          <Label>Profession</Label>
          <Select
            name="profession"
            value={profileData.profession}
            onChange={handleChange}
          >
            <option value="">Select Profession</option>
            {professions.map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </Field>

        {profileData.profession === "Other (Custom Input)" && (
          <Field>
            <Label>Custom Profession</Label>
            <Input
              name="customProfession"
              placeholder="Enter your profession"
              value={profileData.customProfession}
              onChange={handleChange}
            />
          </Field>
        )}

        <Button type="submit">💾 Save Profile</Button>
      </Form>

      <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
        <Button type="button" onClick={() => navigate("/change-password")}>
          🔒 Change Password
        </Button>
      </div>
    </Container>
  );
}
